<template>
  <div class="settings-org-section">
    <SettingsSectionHeader
      title="Positions"
      subtitle="Job titles and position records"
      icon="pi-briefcase"
    >
      <template #actions>
        <Button
          v-if="canCreate"
          label="Add"
          icon="pi pi-plus"
          size="small"
          :disabled="mutating"
          @click="openCreate"
        />
        <Button
          icon="pi pi-refresh"
          label="Refresh"
          size="small"
          severity="secondary"
          outlined
          :loading="loading"
          @click="loadItems"
        />
      </template>
    </SettingsSectionHeader>

    <div class="settings-org-section__body">
      <div v-if="loading && !items.length" class="settings-org-section__state">
        <ProgressSpinner style="width: 2rem; height: 2rem" strokeWidth="4" />
      </div>

      <Message v-else-if="error" severity="error" :closable="false" class="settings-org-section__message">
        <div class="flex flex-col sm:flex-row sm:items-center gap-3">
          <span class="flex-1">{{ error }}</span>
          <Button label="Retry" size="small" severity="danger" outlined @click="loadItems" />
        </div>
      </Message>

      <template v-else>
        <SettingsOrgReferenceToolbar
          v-model:show-inactive="showInactive"
          v-model:search-query="searchQuery"
          :count-label="countLabel"
          inactive-input-id="positions-show-inactive"
          search-placeholder="Search positions..."
          :loading="loading"
        />

        <div v-if="!filteredItems.length" class="settings-org-section__empty">
          <i class="pi pi-briefcase settings-org-section__empty-icon" aria-hidden="true" />
          <p>{{ emptyMessage }}</p>
          <Button
            v-if="searchQuery"
            label="Clear search"
            size="small"
            severity="secondary"
            text
            @click="searchQuery = ''"
          />
        </div>

        <DataTable
          v-else
          :value="filteredItems"
          size="small"
          dataKey="id"
          sort-mode="single"
          removable-sort
          :sort-field="sortField"
          :sort-order="sortOrder"
          @sort="onSort"
          :rowClass="rowClass"
        >
          <Column field="name" header="Name" sortable style="min-width: 10rem">
            <template #body="{ data }">
              <span class="font-medium">{{ data.name }}</span>
            </template>
          </Column>
          <Column v-if="showInactive" header="Status" style="width: 6.5rem">
            <template #body="{ data }">
              <SettingsOrgReferenceStatusTag :item="data" />
            </template>
          </Column>
          <Column field="department_name" header="Department" sortable style="min-width: 9rem">
            <template #body="{ data }">
              <Tag
                v-if="data.department_id == null"
                value="Global"
                severity="info"
              />
              <Tag
                v-else-if="data.department_name"
                :value="data.department_name"
                :style="getDepartmentTagStyle(
                  data.department_id,
                  data.department_name,
                  data.department_tag_color,
                  data.department_tag_text_color
                )"
              />
              <span v-else class="text-sm text-surface-600 dark:text-surface-400">—</span>
            </template>
          </Column>
          <Column field="employees_count" header="Employees" sortable style="width: 6rem">
            <template #body="{ data }">
              <span class="text-sm text-surface-600 dark:text-surface-400">
                {{ data.employees_count ?? 0 }}
              </span>
            </template>
          </Column>
          <Column header="Actions" style="width: 11rem">
            <template #body="{ data }">
              <div class="crc-table-row-actions">
                <Button
                  v-if="canUpdate"
                  icon="pi pi-pencil"
                  size="small"
                  severity="secondary"
                  outlined
                  rounded
                  class="crc-table-action-btn"
                  v-tooltip.top="'Edit'"
                  :disabled="mutating"
                  @click="openEdit(data)"
                />
                <Button
                  v-if="canStatusChange && isOrgReferenceActive(data)"
                  icon="pi pi-ban"
                  size="small"
                  severity="warn"
                  outlined
                  rounded
                  class="crc-table-action-btn"
                  v-tooltip.top="'Deactivate'"
                  :disabled="mutating"
                  @click="confirmDeactivate(data)"
                />
                <Button
                  v-if="canDelete"
                  icon="pi pi-trash"
                  size="small"
                  severity="danger"
                  outlined
                  rounded
                  class="crc-table-action-btn"
                  v-tooltip.top="'Delete'"
                  :disabled="mutating"
                  @click="confirmDelete(data)"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </template>
    </div>

    <PositionEditModal
      v-model:visible="editModalVisible"
      :position="selectedItem"
      :saving="mutating"
      :save-error="saveError"
      :can-create="canCreate"
      :can-edit="canUpdate"
      :can-delete="canDelete"
      :can-set-department="canCreate || canUpdate"
      :department-options="departmentOptions"
      @save="handleSave"
      @delete="confirmDeleteById"
      @department-created="loadDepartments"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Message from 'primevue/message';
import ProgressSpinner from 'primevue/progressspinner';
import SettingsSectionHeader from '../SettingsSectionHeader.vue';
import SettingsOrgReferenceToolbar from '../SettingsOrgReferenceToolbar.vue';
import SettingsOrgReferenceStatusTag from '../SettingsOrgReferenceStatusTag.vue';
import PositionEditModal from '@/components/modals/PositionEditModal.vue';
import { useSettingsEntityList } from '../../composables/useSettingsEntityList';
import {
  buildOrgReferenceCountLabel,
  isOrgReferenceActive,
  orgReferenceRowClass,
  sortOrgReferenceRows,
} from '../../utils/settingsOrgReferenceUtils';
import { fetchDepartmentsList } from '@/api/departmentsApi';
import {
  createPosition,
  deletePosition,
  fetchPositions,
  updatePosition,
  updatePositionStatus,
} from '@/api/positionsApi';
import { getDepartmentTagStyle } from '@/utils/tagColors';
import { buildPositionDeleteMessage } from '@/utils/orgReferenceDeleteConfirm';
import { useEntityPermissions } from '@/composables/useAuth';
import { useClientTableSort } from '@/utils/tableSort';

defineOptions({ name: 'SettingsPositionsSection' });

const { sortField, sortOrder, onSort } = useClientTableSort('name');

defineProps({
  section: { type: Object, default: null },
});

defineEmits(['close-settings']);

const toast = useToast();
const confirm = useConfirm();
const { canCreate, canUpdate, canDelete, canStatusChange } = useEntityPermissions('positions');
const { showInactive, afterMutation } = useSettingsEntityList();

const items = ref([]);
const departmentsList = ref([]);
const loading = ref(false);
const error = ref('');
const mutating = ref(false);
const searchQuery = ref('');
const editModalVisible = ref(false);
const selectedItem = ref(null);
const saveError = ref(null);

// Only active departments are loaded, so a position sitting in a deactivated one
// needs its own entry or the editor would silently drop the assignment on save.
const departmentOptions = computed(() => {
  const options = departmentsList.value.map((dept) => ({ id: dept.id, name: dept.name }));
  const currentId = selectedItem.value?.department_id;
  if (currentId != null && !options.some((option) => Number(option.id) === Number(currentId))) {
    const name = selectedItem.value?.department_name || `Department #${currentId}`;
    options.unshift({ id: Number(currentId), name: `${name} (inactive)` });
  }
  return options;
});

const visibleItems = computed(() => {
  if (showInactive.value) return items.value;
  return items.value.filter(isOrgReferenceActive);
});

const filteredItems = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  const base = query
    ? visibleItems.value.filter((item) => {
        const haystack = [
          item.name,
          item.department_name,
          item.department_id == null ? 'global' : '',
          isOrgReferenceActive(item) ? 'active' : 'inactive',
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(query);
      })
    : visibleItems.value;
  return sortOrgReferenceRows(base);
});

const countLabel = computed(() =>
  buildOrgReferenceCountLabel(visibleItems.value, 'positions', showInactive.value)
);

const emptyMessage = computed(() => {
  if (searchQuery.value) return 'No positions match your search.';
  if (showInactive.value) return 'No positions found. Click Add to create one.';
  return 'No active positions yet. Click Add to create one.';
});

const rowClass = (data) => orgReferenceRowClass(data);

const notifySuccess = (detail) => {
  toast.add({ severity: 'success', summary: 'Success', detail, life: 2500 });
};

const notifyError = (err, fallback) => {
  toast.add({
    severity: 'error',
    summary: 'Error',
    detail: err?.message || fallback,
    life: 3500,
  });
};

const afterMutationReload = () => afterMutation(loadItems);

const loadDepartments = async () => {
  try {
    const list = await fetchDepartmentsList({ active: '1' });
    departmentsList.value = Array.isArray(list) ? list : [];
  } catch {
    departmentsList.value = [];
  }
};

const loadItems = async () => {
  loading.value = true;
  error.value = '';
  try {
    await loadDepartments();
    const { positions } = await fetchPositions({
      page: 1,
      limit: 200,
      sortField: 'name',
      sortOrder: 'ASC',
      include_inactive: showInactive.value ? 1 : 0,
    });
    items.value = Array.isArray(positions) ? positions : [];
  } catch (err) {
    error.value = err?.message || 'Failed to load positions.';
    items.value = [];
  } finally {
    loading.value = false;
  }
};

const openCreate = () => {
  selectedItem.value = null;
  saveError.value = null;
  editModalVisible.value = true;
};

const openEdit = (item) => {
  selectedItem.value = item;
  saveError.value = null;
  editModalVisible.value = true;
};

const handleSave = async (payload) => {
  mutating.value = true;
  saveError.value = null;
  try {
    if (payload.id) {
      await updatePosition({
        position_id: payload.id,
        name: payload.name?.trim(),
        department_id: payload.department_id ?? null,
        is_active: payload.is_active ? 1 : 0,
      });
      notifySuccess('Position updated');
    } else {
      await createPosition({
        name: payload.name?.trim(),
        department_id: payload.department_id ?? null,
        is_active: payload.is_active ? 1 : 0,
      });
      notifySuccess('Position created');
    }
    editModalVisible.value = false;
    await afterMutationReload();
  } catch (err) {
    // Dialog stays open: surface the reason inline instead of losing the draft.
    saveError.value = { code: err?.code || null, message: err?.message || 'Failed to save position.' };
  } finally {
    mutating.value = false;
  }
};

const handleDelete = async ({ id }) => {
  mutating.value = true;
  try {
    await deletePosition({ position_id: id });
    notifySuccess('Position deleted');
    editModalVisible.value = false;
    await afterMutationReload();
  } catch (err) {
    saveError.value = { code: err?.code || null, message: err?.message || 'Failed to delete position.' };
    notifyError(err, 'Failed to delete position.');
  } finally {
    mutating.value = false;
  }
};

const confirmDeactivate = (item) => {
  confirm.require({
    header: 'Deactivate position',
    message: `"${item.name}" will be hidden from profile options. Linked employees keep their records.`,
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Deactivate',
    acceptClass: 'p-button-warning',
    accept: () => deactivateItem(item),
  });
};

const deactivateItem = async (item) => {
  mutating.value = true;
  try {
    await updatePositionStatus({
      position_id: item.id,
      is_active: false,
    });
    notifySuccess('Position deactivated');
    await afterMutationReload();
  } catch (err) {
    notifyError(err, 'Failed to deactivate position.');
  } finally {
    mutating.value = false;
  }
};

const confirmDelete = (item) => {
  confirm.require({
    header: 'Delete position',
    message: buildPositionDeleteMessage({
      name: item.name,
      employeeCount: Number(item.employees_count ?? 0),
    }),
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    acceptClass: 'p-button-danger',
    accept: () => handleDelete({ id: item.id }),
  });
};

const confirmDeleteById = ({ id }) => {
  const item = items.value.find((row) => Number(row.id) === Number(id));
  confirmDelete(item || { id, name: selectedItem.value?.name || 'this position' });
};

onMounted(() => {
  loadItems();
});

watch(showInactive, () => {
  loadItems();
});
</script>

<style scoped>
.settings-org-section {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}

.settings-org-section__body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 1rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.settings-org-section__state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 12rem;
}

.settings-org-section__message {
  margin: 0;
}

.settings-org-section__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 10rem;
  border: 1px dashed var(--crc-panel-border, var(--surface-border));
  border-radius: 0.75rem;
  color: var(--crc-text-muted, var(--text-color-secondary));
  text-align: center;
}

.settings-org-section__empty-icon {
  font-size: 1.5rem;
}

.settings-org-section__empty p {
  margin: 0;
  font-size: 0.875rem;
}

:deep(.settings-org-section__row--inactive) {
  opacity: 0.65;
}
</style>
